import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, Save, TestTube, ArrowLeft, Server, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SMTPSettings = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [existingSettings, setExistingSettings] = useState(null);
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'OddsChamp Support'
  });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchSettings();
  }, [user, navigate]);

  const fetchSettings = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/admin/smtp-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setExistingSettings(response.data);
        setSettings({
          smtp_host: response.data.smtp_host || '',
          smtp_port: response.data.smtp_port || 587,
          smtp_username: response.data.smtp_username || '',
          smtp_password: '', // Keep empty, will be filled if user changes
          smtp_from_email: response.data.smtp_from_email || '',
          smtp_from_name: response.data.smtp_from_name || 'OddsChamp Support'
        });
      }
    } catch (error) {
      console.error('Error fetching SMTP settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const token = localStorage.getItem('session_token');

    try {
      const dataToSend = { ...settings };
      
      // If password is empty and we have existing settings, use placeholder
      // Backend will ignore it and keep the old password
      if (!dataToSend.smtp_password && existingSettings) {
        dataToSend.smtp_password = '********';
      }
      
      await axios.post(`${API}/admin/smtp-settings`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SMTP settings saved successfully!');
      
      // Refresh settings to get updated data
      fetchSettings();
    } catch (error) {
      console.error('Error saving SMTP settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save SMTP settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    const token = localStorage.getItem('session_token');

    try {
      await axios.post(`${API}/admin/smtp-settings/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test email sent! Check your inbox.');
    } catch (error) {
      console.error('Error testing SMTP:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-white text-lg">Loading SMTP settings...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center mb-8">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              className="text-white hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-3xl sm:text-4xl font-bold text-white">SMTP Settings</h1>
          </div>

          <Card className="glass-dark border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Server className="w-6 h-6 mr-2 text-blue-400" />
                Email Server Configuration
              </CardTitle>
              <p className="text-gray-400 text-sm">
                Configure SMTP settings to enable email verification and password reset functionality
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSave} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* SMTP Host */}
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300 flex items-center">
                      <Server className="w-4 h-4 mr-1" />
                      SMTP Host
                    </label>
                    <Input
                      type="text"
                      placeholder="smtp.gmail.com"
                      value={settings.smtp_host}
                      onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                      className="bg-white/5 border-white/10 text-white"
                      required
                    />
                    <p className="text-xs text-gray-500">Example: smtp.gmail.com, smtp.office365.com</p>
                  </div>

                  {/* SMTP Port */}
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">SMTP Port</label>
                    <Input
                      type="number"
                      placeholder="587"
                      value={settings.smtp_port}
                      onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) })}
                      className="bg-white/5 border-white/10 text-white"
                      required
                    />
                    <p className="text-xs text-gray-500">Common ports: 587 (TLS), 465 (SSL), 25</p>
                  </div>
                </div>

                {/* SMTP Username */}
                <div className="space-y-2">
                  <label className="text-sm text-gray-300">SMTP Username</label>
                  <Input
                    type="text"
                    placeholder="your-email@domain.com"
                    value={settings.smtp_username}
                    onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    required
                  />
                  <p className="text-xs text-gray-500">Usually your full email address</p>
                </div>

                {/* SMTP Password */}
                <div className="space-y-2">
                  <label className="text-sm text-gray-300 flex items-center">
                    <Lock className="w-4 h-4 mr-1" />
                    SMTP Password
                  </label>
                  <Input
                    type="password"
                    placeholder={existingSettings ? "Leave empty to keep current password" : "Enter SMTP password"}
                    value={settings.smtp_password}
                    onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                  <p className="text-xs text-gray-500">
                    For Gmail, use an App Password. Leave empty to keep current password.
                  </p>
                </div>

                {/* From Email */}
                <div className="space-y-2">
                  <label className="text-sm text-gray-300 flex items-center">
                    <Mail className="w-4 h-4 mr-1" />
                    Support Email Address
                  </label>
                  <Input
                    type="email"
                    placeholder="support@oddschamp.com"
                    value={settings.smtp_from_email}
                    onChange={(e) => setSettings({ ...settings, smtp_from_email: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    required
                  />
                  <p className="text-xs text-gray-500">Email address that will appear as sender</p>
                </div>

                {/* From Name */}
                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Support Name</label>
                  <Input
                    type="text"
                    placeholder="OddsChamp Support"
                    value={settings.smtp_from_name}
                    onChange={(e) => setSettings({ ...settings, smtp_from_name: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    required
                  />
                  <p className="text-xs text-gray-500">Name that will appear as sender</p>
                </div>

                {/* Info Box */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-300">
                      <p className="font-medium mb-1">Gmail Users:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Enable 2-factor authentication on your Google account</li>
                        <li>Go to Google Account → Security → 2-Step Verification → App passwords</li>
                        <li>Generate an app password and use it here</li>
                      </ol>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={saving}
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {saving ? 'Saving...' : 'Save Settings'}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleTest}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                    disabled={testing || !settings.smtp_host}
                  >
                    <TestTube className="w-5 h-5 mr-2" />
                    {testing ? 'Testing...' : 'Test Email'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default SMTPSettings;
